import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';

const socket = io('https://teamo.co.za', {
  withCredentials: true,
});

export default function ChatWindow({ chatType, currentUser, loadingUser }) {
  const { chatId: routeChatId, groupId, userId } = useParams();
  const chatId = chatType === 'group' ? groupId || routeChatId : userId || routeChatId;

  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  // Fetch existing messages on load
  useEffect(() => {
    if (!chatId) return;

    async function fetchMessages() {
      setLoading(true);
      setError(null);
      try {
        const url =
          chatType === 'group'
            ? `/api/chat/group-messages/${chatId}`
            : `/api/chat/direct-messages/${chatId}`;

        const token = localStorage.getItem('authToken');
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error('Failed to fetch messages');

        const data = await res.json();
        setMessages(data);
      } catch (err) {
        console.error('Error loading messages:', err);
        setError('Failed to load messages');
      } finally {
        setLoading(false);
      }
    }

    fetchMessages();
  }, [chatType, chatId]);

  // Mark direct messages as read
  useEffect(() => {
    if (chatType !== 'direct' || messages.length === 0) return;

    const unreadMessageIds = messages
      .filter(msg => String(msg.recipient_id) === String(currentUser?.id) && !msg.read_at)
      .map(msg => msg.id);

    if (unreadMessageIds.length > 0) {
      const token = localStorage.getItem('authToken');

      fetch('/api/chat/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message_ids: unreadMessageIds }),
      })
        .then(res => res.json())
        .then(data => console.log('✅ Marked messages as read:', data))
        .catch(err => console.error('❌ Failed to mark messages as read:', err));
    }
  }, [chatType, messages, currentUser?.id]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

// Socket.IO: connect, join room, receive messages
useEffect(() => {
  if (!chatId || !currentUser?.id) return;

  socket.connect();
  if (chatType === 'group') {
    socket.emit('join_chat', chatId);
    console.log(`🪐 Joined group chat room ${chatId}`);
} else if (chatType === 'direct') {
    socket.emit('join_chat', String(currentUser.id));
    console.log(`🪐 Joined direct chat room for user ID ${currentUser.id}`);
}

  const handleIncomingMessage = (incomingMessage) => {
    console.log('📩 Received via socket:', incomingMessage);
    setMessages(prev => {
      const exists = prev.some(msg => msg.id === incomingMessage.id);
      if (exists) {
        console.log('⚠️ Duplicate message ignored:', incomingMessage.id);
        return prev;
      }
      const updatedMessages = [...prev, incomingMessage];
      console.log('🛠️ Message appended:', incomingMessage);
      console.log('🛠️ Messages after update:', updatedMessages);
      return updatedMessages;
    });
  };

  // ✅ Add this to confirm listener attachment
  socket.on('receive_message', handleIncomingMessage);
  console.log('🛠️ Socket listener for receive_message attached');

  return () => {
    socket.off('receive_message', handleIncomingMessage);
    socket.disconnect();
    console.log(`🚪 Left chat room ${chatId}`);
  };
}, [chatId, currentUser?.id]);

  async function handleSend() {
    if (!newMsg.trim()) return;

    if (chatType === 'direct' && !chatId) {
      alert('Recipient ID is missing for direct message');
      return;
    }

    const token = localStorage.getItem('authToken');

    const body = {
      type: chatType,
      content: newMsg,
    };

    if (chatType === 'group') {
      body.group_id = parseInt(chatId, 10);
    } else if (chatType === 'direct') {
      body.recipient_id = parseInt(chatId, 10);
    }

    try {
      const res = await fetch('/api/chat/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Failed to send message');

      const sentMsg = await res.json();

      // Emit the message via socket for real-time updates
      console.log('Emitting send_message with data:', sentMsg);
       console.log('🪐 Emitted join_chat for:', chatId);
       console.log('Current socket id:', socket.id);
       console.log('Current user id:', currentUser?.id);

      socket.emit('send_message', {
        ...sentMsg,

       chatId: chatId, // ensure chatId is included for room routing
      });

      // Optimistically add to local state for instant UI update
      setMessages(prev => [...prev, sentMsg]);
      setNewMsg('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  async function handleDeleteMessage(messageId) {
    const token = localStorage.getItem('authToken');
    try {
      const res = await fetch(`/api/chat/message/${messageId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to delete message');

      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete message');
    }
  }

  if (loadingUser || !currentUser || !currentUser.id) {
    return (
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        Loading chat...
      </div>
    );
  }

useEffect(() => {
  const markGroupMessagesAsRead = async () => {
    try {
      await fetch(`/api/chat/group-messages/${chatId}/read`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
    } catch (err) {
      console.error("Failed to mark group messages as read", err);
    }
  };

  markGroupMessagesAsRead();
}, [chatId]);


  return (
    <div
      style={{
        border: '1px solid #ccc',
        padding: 10,
        maxWidth: 600,
        margin: '20px auto',
      }}
    >
      {/* Navigation buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          Home
        </button>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          Back
        </button>
      </div>

      {/* Message list */}
      <div
        style={{
          maxHeight: 400,
          overflowY: 'auto',
          marginBottom: 10,
          backgroundColor: '#fafafa',
          padding: 10,
          borderRadius: 4,
        }}
      >
        {loading && <div style={{ textAlign: 'center' }}>Loading messages...</div>}
        {error && <div style={{ color: 'red', textAlign: 'center' }}>{error}</div>}
        {!loading && !error && messages.length === 0 && (
          <div style={{ color: '#666', textAlign: 'center' }}>No messages yet</div>
        )}

        {messages.map(m => {
          const isOwnMessage = String(m.sender_id) === String(currentUser?.id);

          return (
            <div
              key={m.id}
              style={{ marginBottom: 8, textAlign: isOwnMessage ? 'right' : 'left' }}
            >
              {!isOwnMessage && m.name && m.surname && (
                <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 2 }}>
                  {m.name} {m.surname}
                </div>
              )}
              <div
                style={{
                  display: 'inline-block',
                  backgroundColor: isOwnMessage ? '#d1e7dd' : '#e2e3e5',
                  color: '#333',
                  padding: '6px 10px',
                  borderRadius: 12,
                  maxWidth: '70%',
                  wordBreak: 'break-word',
                  position: 'relative',
                }}
              >
                {m.content}
                {isOwnMessage && (
                  <>
                    <button
                      onClick={() => handleDeleteMessage(m.id)}
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 4,
                        background: 'none',
                        border: 'none',
                        color: '#c00',
                        fontSize: 14,
                        cursor: 'pointer',
                        padding: 0,
                        lineHeight: 1,
                      }}
                      title="Delete message"
                    >
                      ×
                    </button>
                    {m.read_by_recipient_at ? (
                      <span
                        title="Read"
                        style={{
                          display: 'inline-block',
                          width: 14,
                          height: 14,
                          backgroundColor: '#28a745',
                          borderRadius: '50%',
                          marginLeft: 6,
                          verticalAlign: 'middle',
                        }}
                      />
                    ) : (
                      <span
                        title="Sent"
                        style={{
                          display: 'inline-block',
                          width: 14,
                          height: 14,
                          backgroundColor: '#6c757d',
                          borderRadius: '50%',
                          marginLeft: 6,
                          verticalAlign: 'middle',
                        }}
                      />
                    )}
                  </>
                )}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: '#999',
                  marginTop: 2,
                  fontFamily: 'monospace',
                }}
              >
                {new Date(m.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Message input */}
      <textarea
        value={newMsg}
        onChange={e => setNewMsg(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type a message..."
        rows={2}
        style={{
          width: '100%',
          resize: 'none',
          padding: 8,
          fontSize: 14,
          borderRadius: 4,
          border: '1px solid #ccc',
          boxSizing: 'border-box',
          marginBottom: 6,
        }}
      />
      <button
        onClick={handleSend}
        disabled={newMsg.trim() === ''}
        style={{
          width: '100%',
          padding: '8px 0',
          fontSize: 16,
          borderRadius: 4,
          border: 'none',
          backgroundColor: newMsg.trim() === '' ? '#6c757d' : '#007bff',
          color: 'white',
          cursor: newMsg.trim() === '' ? 'not-allowed' : 'pointer',
        }}
      >
        Send
      </button>
    </div>
  );
}
